'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { readConsent, writeConsent, CONSENT_REOPEN_EVENT } from '@/lib/cookie-consent';

// Matches the transition duration on the banner below. The banner stays
// mounted for this long after a decision so the swoop-out can finish playing.
const SWOOP_MS = 300;

export function CookieConsentBanner() {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  // Drives the slide transform independently of `open` so we can render the
  // banner off-screen for one frame (swoop in) and keep it mounted while it
  // slides back down (swoop out) before `open` finally unmounts it.
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analyticsOptIn, setAnalyticsOptIn] = useState(false);

  useEffect(() => {
    setMounted(true);
    const existing = readConsent();
    if (existing) {
      setAnalyticsOptIn(existing.analytics);
      return;
    }
    // No decision on file: show the banner. Default the customize toggle
    // to off so non-essential cookies aren't pre-ticked (GDPR requirement).
    setAnalyticsOptIn(false);
    setOpen(true);
  }, []);

  // Allow the footer "Cookie preferences" link (or anywhere else) to reopen the
  // banner after the first decision so users can review or withdraw consent as
  // easily as they gave it. Prefill the toggle from the stored choice and
  // expand the detail view so the categories are visible.
  useEffect(() => {
    const reopen = () => {
      const existing = readConsent();
      setAnalyticsOptIn(existing?.analytics ?? false);
      setShowDetails(true);
      setOpen(true);
    };
    window.addEventListener(CONSENT_REOPEN_EVENT, reopen);
    return () => window.removeEventListener(CONSENT_REOPEN_EVENT, reopen);
  }, []);

  // Once the banner is mounted (open), flip to the on-screen position on the
  // next frame so the browser paints the off-screen state first and animates
  // the swoop in. A double rAF guarantees the initial frame is committed.
  useEffect(() => {
    if (!open) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setVisible(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [open]);

  if (!mounted || !open) return null;

  // Persist the choice, play the swoop-out, then unmount once it finishes.
  const close = (persist: () => void) => {
    persist();
    setVisible(false);
    window.setTimeout(() => {
      setOpen(false);
      setShowDetails(false);
    }, SWOOP_MS);
  };

  const acceptAll = () => close(() => writeConsent({ analytics: true }));

  const rejectNonEssential = () => close(() => writeConsent({ analytics: false }));

  const saveCustom = () => close(() => writeConsent({ analytics: analyticsOptIn }));

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-heading"
      aria-describedby="cookie-consent-description"
      className={cn(
        'fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6',
        'transition-transform duration-300 ease-out motion-reduce:transition-none',
        visible ? 'translate-y-0' : 'translate-y-full',
      )}
    >
      <div className="mx-auto max-w-2xl rounded-lg border border-border bg-card/95 shadow-lg backdrop-blur p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 id="cookie-consent-heading" className="text-base font-semibold">
              Cookies on supa.place
            </h2>
            <p
              id="cookie-consent-description"
              className="mt-1.5 text-sm text-muted-foreground leading-relaxed"
            >
              We use cookies and similar technologies to keep you logged in,
              protect the site (including with Google reCAPTCHA), remember
              preferences like your currency, and only with your consent to
              understand how the site is used. Every one we set, and those
              Google reCAPTCHA and Stripe set on our pages, is listed in{' '}
              <Link
                href="/privacy-policy#cookies-and-tracking"
                className="text-link underline decoration-link/40 underline-offset-4 hover:decoration-link"
              >
                Cookies and Tracking
              </Link>{' '}
              in our Privacy Policy.
            </p>
          </div>
          <button
            type="button"
            aria-label="Reject non-essential cookies and close"
            onClick={rejectNonEssential}
            className="shrink-0 -m-1.5 p-1.5 text-muted-foreground hover:text-foreground rounded-md transition-colors"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {showDetails && (
          <div className="mt-4 space-y-3 border-t border-border pt-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium">Strictly necessary and preferences</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Required for logging in, security (including Google reCAPTCHA), checkout and your cart, and remembering choices you make, such as your currency. Always on.
                </p>
              </div>
              <Switch
                checked
                disabled
                aria-label="Strictly necessary and preference cookies (always on)"
              />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium">Analytics</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Product analytics (PostHog) so we can see what works and fix what doesn&apos;t. Linked to your account while you&apos;re logged in. If you came through one of our invite links, also remembers which one for 30 days, and if you came through a seller&apos;s payment link or buy button, which one in this tab for up to a day.
                </p>
              </div>
              <Switch
                checked={analyticsOptIn}
                onCheckedChange={setAnalyticsOptIn}
                aria-label="Allow analytics cookies"
              />
            </div>
          </div>
        )}

        <div
          className={cn(
            'mt-5 flex flex-col gap-2 sm:flex-row',
            showDetails ? 'sm:justify-end' : 'sm:items-center sm:justify-between',
          )}
        >
          {showDetails ? (
            <>
              <Button
                variant="ghost"
                onClick={() => setShowDetails(false)}
                className="sm:order-1"
              >
                Back
              </Button>
              <Button onClick={saveCustom} className="sm:order-2">
                Save preferences
              </Button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setShowDetails(true)}
                className="self-start text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground sm:self-auto"
              >
                Customize
              </button>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                <Button variant="outline" onClick={rejectNonEssential}>
                  Reject non-essential
                </Button>
                <Button onClick={acceptAll}>Accept all</Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
